import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Alert, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Grid } from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import FormProvider from '../form/FormProvider';
import TextField from '../form/TextField';
import PhoneField from '../form/PhoneField';

const schema = z.object({
  name: z.string().min(1, 'Office name is required'),
  nmls: z.string().min(1, 'NMLS is required'),
  compId: z.string().optional().default(''),
  address: z.object({
    address1: z.string().optional().default(''),
    address2: z.string().optional().default(''),
    city: z.string().optional().default(''),
    state: z.string().max(2).optional().default(''),
    postalCode: z.string().optional().default(''),
  }),
  phone: z.string().optional().default(''),
  email: z.string().optional().default(''),
});

/**
 * Modal for editing a satellite office.
 */
const EditOfficeModal = ({ office, canEditNmls, onSave, onClose }) => {
  const methods = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      name: office.name || '', nmls: office.nmls || '', compId: office.compId || '',
      address: {
        address1: office.address?.address1 || '', address2: office.address?.address2 || '',
        city: office.address?.city || '', state: office.address?.state || '',
        postalCode: office.address?.postalCode || '',
      },
      phone: office.phone || '', email: office.email || '',
    },
  });
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (data) => {
    setError('');
    setSubmitting(true);
    try {
      await onSave({ officeId: office.id, ...data });
      onClose();
    } catch (err) {
      const msg = err.data?.title
        || err.data?.[0]?.description
        || (Array.isArray(err.data) ? err.data.map(e => e.description).join(', ') : null)
        || 'Failed to update office';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Edit Satellite Office</DialogTitle>
      <FormProvider methods={methods} onSubmit={handleSubmit}>
        <DialogContent dividers>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid size={{ xs: 6 }}><TextField name="name" label="Office Name" required size="small" /></Grid>
            <Grid size={{ xs: 6 }}>
              <TextField name="nmls" label="NMLS" required size="small" disabled={!canEditNmls} helperText={!canEditNmls ? 'NMLS can only be changed by a Lender Admin.' : undefined} />
            </Grid>
          </Grid>
          <TextField name="address.address1" label="Street Address" size="small" placeholder="Street address" sx={{ mb: 2 }} />
          <TextField name="address.address2" label="Address Line 2" size="small" placeholder="Suite, unit, etc." sx={{ mb: 2 }} />
          <Grid container spacing={1.5} sx={{ mb: 2 }}>
            <Grid size={{ xs: 5 }}><TextField name="address.city" label="City" size="small" /></Grid>
            <Grid size={{ xs: 3 }}><TextField name="address.state" label="State" size="small" placeholder="CA" inputProps={{ maxLength: 2 }} /></Grid>
            <Grid size={{ xs: 4 }}><TextField name="address.postalCode" label="Postal Code" size="small" placeholder="12345" /></Grid>
          </Grid>
          <TextField name="compId" label="Compensation ID" size="small" disabled={!canEditNmls} placeholder="Assigned by Lender Admin" helperText={!canEditNmls ? 'Comp ID can only be changed by a Lender Admin.' : undefined} sx={{ mb: 2 }} />
          <Grid container spacing={2}>
            <Grid size={{ xs: 6 }}><PhoneField name="phone" label="Phone" size="small" /></Grid>
            <Grid size={{ xs: 6 }}><TextField name="email" label="Email" type="email" size="small" /></Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={submitting}>Cancel</Button>
          <Button type="submit" variant="contained" disabled={submitting} startIcon={submitting ? <CircularProgress size={16} /> : <SaveIcon />}>
            {submitting ? 'Saving…' : 'Save Changes'}
          </Button>
        </DialogActions>
      </FormProvider>
    </Dialog>
  );
};

export default EditOfficeModal;
